// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import * as React from 'react';
import { useTranslation } from 'react-i18next';

import {
    IAddressInfo,
    IClaimOrder,
} from './../../businessLogic/sagas/serviceRequest';
import Collapsible from '../Collapsible';
import Styles from './addressDetails.module.scss';

interface PropsType {
    data: IClaimOrder;
}

export default function AddressDetails(props: PropsType) {
    const { t } = useTranslation();
    return (
        <Collapsible
            colorOnOpen={'white'}
            colorOnClose={'alabaster'}
            chevronColor={'black'}
            header={RenderHeader()}
            className={Styles.addressDetails}
            tableName={t('addresses')}
        >
            {RenderBody(props)}
        </Collapsible>
    );
}

function composeAddress(data: IAddressInfo) {
    const addresses: string[] = [];
    if (data.addressLine1) addresses.push(data.addressLine1);
    if (data.addressLine2) addresses.push(data.addressLine2);
    if (data.addressLine3) addresses.push(data.addressLine3);
    return `${addresses.join(', ')}, ${data.city || ''} ${data.region || ''} ${data.postalCode || ''}`;
}

function RenderHeader() {
    const { t } = useTranslation();

    return (
        <div className={Styles.header}>
            <div className={Styles.order}>
                <div className={Styles.headerText}>{t('addresses')}</div>
            </div>
        </div>
    );
}

function RenderBody(props: PropsType) {
    const { t } = useTranslation();

    const claimOrder: IClaimOrder = props.data;
    const { payments, shipments, serviceLocationDetails, lineItems } = claimOrder;
    const { billTo } = payments;
    const { shipTo } = shipments;
    const hasServicelocationDetails = serviceLocationDetails && Object.keys(serviceLocationDetails).length ? true : false;
    const hasSecondaryContact = hasServicelocationDetails && (serviceLocationDetails.secondaryContactName || serviceLocationDetails.secondaryContactPhoneNumber);

    const addresses: any[] = [];

    // special case for travel SR and second SR for partner
    if(hasServicelocationDetails && lineItems.length > 1){
        addresses.push(
            {
                label: 'partner_shipping_address',
                fullName: `${shipTo.firstName} ${shipTo.lastName}`,
                email: shipTo.emailId,
                phoneNumber: shipTo.phoneNumber,
                address: composeAddress(shipTo),
            }
        );
    }

    if(hasServicelocationDetails){
        addresses.push(
            {
                label: 'service_location_address',
                fullName: `${serviceLocationDetails?.firstName} ${serviceLocationDetails?.lastName}`,
                email: serviceLocationDetails?.emailId,
                phoneNumber: serviceLocationDetails?.phoneNumber,
                address: composeAddress(serviceLocationDetails),
            }
        );
    }
    else {
        addresses.push(
            {
                label: 'shipping_address',
                fullName: `${shipTo.firstName} ${shipTo.lastName}`,
                email: shipTo.emailId,
                phoneNumber: shipTo.phoneNumber,
                address: composeAddress(shipTo),
            }
        );
        addresses.push(
            {
                label: 'billing_address',
                fullName: `${billTo.firstName} ${billTo.lastName}`,
                email: billTo.emailId,
                phoneNumber: billTo.phoneNumber,
                address: composeAddress(billTo),
            }
        );
    }

    return (
        <div className={Styles.body}>
            {addresses.map((address, key) => (
                <div key={key} className={Styles.addressSection}>
                    <div className={Styles.addressLabel}>{t(address.label)}</div>
                    <div className={Styles.addressName}>{address.fullName}</div>
                    <div className={Styles.addressName}>{address.email}</div>
                    <div className={Styles.addressName}>{address.phoneNumber}</div>
                    <div className={Styles.addressValue}>{address.address}</div>
                </div>
            ))}
            {
                hasSecondaryContact &&
                <div className={Styles.addressSection}>
                    <div className={Styles.addressLabel}>{t('secondary_contact')}</div>
                    <div className={Styles.addressName}>{serviceLocationDetails.secondaryContactName}</div>
                    <div className={Styles.addressName}>{serviceLocationDetails.secondaryContactPhoneNumber}</div>
                </div>
            }
        </div>
    );
}